import { Maybe, Workspace } from "types";
import { useOrganization } from "./useOrganization";
import { useList } from "@refinedev/core";
import { useBoundStore } from "store";
import { ALL_WORKSPACES_ID } from "@/constants";

export const useWorkspace = (options?: { organizationId: string }) => {
  const appState = useBoundStore((state) => state.appState);
  const setAppState = useBoundStore((state) => state.setAppState);
  const { organization } = useOrganization({});
  const workspaceOrganizationId = options?.organizationId ?? organization?.id;
  const {
    data: orgWorkspaces,
    isLoading: isLoadingOrgWorkspaces,
    refetch,
  } = useList<Workspace>({
    resource: `api/${workspaceOrganizationId}/workspaces`,
    queryOptions: {
      enabled: Boolean(workspaceOrganizationId),
    },
    errorNotification: false,
  });

  const workspaces = orgWorkspaces?.data ?? [];

  const getWorkspace = (id: string): Maybe<Workspace> =>
    workspaces?.find((x) => x.id === id);

  const changeWorkspace = ({ workspace }) => {
    setAppState({ workspace });
  };

  const storedIsAll = appState.workspace?.id === ALL_WORKSPACES_ID;

  // Restore from store, preserving the "All" sentinel
  const currentWorkspace = storedIsAll
    ? appState.workspace
    : (appState.workspace ?? workspaces?.[0]);

  const isAllWorkspacesSelected = currentWorkspace?.id === ALL_WORKSPACES_ID;

  // Find the "default" workspace for project creation when "All" is selected
  const defaultWorkspace =
    workspaces?.find((w) => w.name === "default") ?? workspaces?.[0];

  const isLoading = isLoadingOrgWorkspaces;

  return {
    workspace: currentWorkspace,
    workspaceId: isAllWorkspacesSelected ? undefined : currentWorkspace?.id,
    workspaces,
    hasWorkspace: workspaces?.length > 0,
    isAllWorkspacesSelected,
    defaultWorkspace,
    isLoading,
    changeWorkspace,
    getWorkspace,
    refetch,
  };
};
